import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { empreendimentoService } from '../../services/empreendimentoService';
import { movimentacaoService, movimentacaoEmpreendimentoService, movimentacaoUnidadeService } from '../../services/movimentacaoService';
import { snapshotEmpreendimentoService, snapshotUnidadeService } from '../../services/snapshotService';
import { Empreendimento, ParteEmpreendimento } from '../../types/empreendimento';
import { SnapshotEmpreendimento, SnapshotUnidade, MovimentacaoEmpreendimento, MovimentacaoUnidade } from '../../types/movimentacao';
import { Building, X, Home, Clock, ChevronLeft, ChevronRight, Search } from 'lucide-react';

// --- Tipos e Interfaces ---

interface UnidadeComSnapshot extends ParteEmpreendimento {
    snapshotData?: SnapshotUnidade;
}

interface Torre {
    id: number;
    nome: string;
    unidadesMatrix: (UnidadeComSnapshot | null)[][];
    maxAndar: number;
    maxApt: number;
}

// --- Componentes Auxiliares e Funções Utilitárias ---

const Spinner: React.FC = () => (
    <div className="flex justify-center items-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
    </div>
);

const formatDate = (dateString: string) => new Date(dateString).toLocaleString('pt-BR', { timeZone: 'UTC' });
const formatDateTime = (dateString: string) => new Date(dateString).toLocaleString('pt-BR', { timeZone: 'UTC' });

const getFaseEmpreendimentoDisplay = (fase: number | string) => {
    const fases = {
        '1': 'Planta',
        '2': 'Obras',
        '3': 'Pronto',
    };
    return fases[String(fase) as keyof typeof fases] || 'Não definida';
};


// --- MODAL 3: Visualizador de Logs (Sem alterações) ---

const VisualizadorLogsModal: React.FC<{
    item: { id: number; nome: string; tipo: 'empreendimento' | 'unidade' };
    dataSnapshot: string;
    onClose: () => void;
}> = ({ item, dataSnapshot, onClose }) => {
    const [logs, setLogs] = useState<MovimentacaoEmpreendimento[] | MovimentacaoUnidade[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLogs = async () => {
            setLoading(true);
            const date = new Date(dataSnapshot);
            
            const primeiroDiaDoMesDoSnapshot = new Date(date.getFullYear(), date.getMonth(), 1);
            const ultimoDiaDoMesAnterior = new Date(primeiroDiaDoMesDoSnapshot.getTime() - 1);
            const primeiroDiaDoMesAnterior = new Date(ultimoDiaDoMesAnterior.getFullYear(), ultimoDiaDoMesAnterior.getMonth(), 1);

            const startDate = primeiroDiaDoMesAnterior;
            const endDate = ultimoDiaDoMesAnterior;
            endDate.setHours(23, 59, 59, 999);

            try {
                let response;
                if (item.tipo === 'empreendimento') {
                    response = await movimentacaoEmpreendimentoService.list({
                        empreendimento: item.id,
                        data_hora_after: startDate.toISOString(),
                        data_hora_before: endDate.toISOString(),
                        limit: 1000
                    });
                } else {
                    response = await movimentacaoUnidadeService.list({
                        unidade: item.id,
                        data_hora_after: startDate.toISOString(),
                        data_hora_before: endDate.toISOString(),
                        limit: 1000
                    });
                }
                setLogs(response.results);
            } catch (error) {
                console.error(`Erro ao buscar logs para ${item.tipo}:`, error);
                toast.error('Erro ao carregar os logs de consolidação.');
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, [item, dataSnapshot]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full flex flex-col max-h-[80vh]">
                <div className="px-6 py-4 border-b flex justify-between items-center">
                    <h3 className="text-xl font-semibold text-gray-900">
                        Logs de Consolidação: {item.nome}
                        <span className="block text-sm font-normal text-gray-500">Movimentações do mês anterior ao snapshot de {formatDate(dataSnapshot)}</span>
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={24} /></button>
                </div>
                <div className="p-6 overflow-y-auto">
                    {loading ? <Spinner /> : (
                        logs.length > 0 ? (
                            <ul className="space-y-3">
                                {logs.map(log => (
                                    <li key={log.id} className="p-3 bg-gray-50 rounded-md border text-sm">
                                        <p className="font-semibold text-gray-800">
                                            <span className="text-indigo-600">{log.campo_alterado_display}:</span> de "{log.valor_antigo}" para "{log.valor_novo}"
                                        </p>
                                        <p className="text-gray-500 text-xs mt-1">
                                            Registrado por <strong>{log.usuario_nome}</strong> em {formatDateTime(log.data_hora)}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        ) : <p className="text-center text-gray-500 py-4">Nenhuma movimentação encontrada no mês anterior.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- MODAL 2: Visualizador de Snapshot do Empreendimento (MODIFICADO) ---
// A unidade (div) agora é clicável

const SnapshotViewerModal: React.FC<{
    snapshot: SnapshotEmpreendimento | null;
    onClose: () => void;
}> = ({ snapshot, onClose }) => {
    const [empreendimento, setEmpreendimento] = useState<Empreendimento | null>(null);
    const [torres, setTorres] = useState<Torre[]>([]);
    const [unidadesAvulsas, setUnidadesAvulsas] = useState<UnidadeComSnapshot[]>([]);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [logItem, setLogItem] = useState<{ id: number; nome: string; tipo: 'empreendimento' | 'unidade' } | null>(null);

    useEffect(() => {
        if (!snapshot) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const empData = await empreendimentoService.getById(snapshot.empreendimento);
                setEmpreendimento(empData);

                const todasAsUnidades = await empreendimentoService.listPartesEmpreendimento(snapshot.empreendimento);
                const snapshotsUnidadeResponse = await snapshotUnidadeService.list({ empreendimento: snapshot.empreendimento, data_snapshot: snapshot.data_snapshot, limit: 1000 });
                
                const snapshotsMap: Map<number, SnapshotUnidade> = new Map(snapshotsUnidadeResponse.results.map((su: SnapshotUnidade) => [su.unidade, su]));

                const unidadesComSnapshot: UnidadeComSnapshot[] = todasAsUnidades.map(u => ({
                    ...u,
                    snapshotData: snapshotsMap.get(u.id)
                }));
                
                const avulsas = unidadesComSnapshot.filter(u => !u.torre);
                const comTorre = unidadesComSnapshot.filter(u => !!u.torre);
                setUnidadesAvulsas(avulsas);
                
                const torresMap: Record<string, UnidadeComSnapshot[]> = {};
                comTorre.forEach(unidade => {
                    const torreNome = `Torre ${unidade.torre}`;
                    if (!torresMap[torreNome]) torresMap[torreNome] = [];
                    torresMap[torreNome].push(unidade);
                });
                
                const torresEstruturadas: Torre[] = Object.entries(torresMap).map(([nome, unidades], index) => {
                    let maxAndar = 0, maxApt = 0;
                    unidades.forEach(u => { if (u.andar > maxAndar) maxAndar = u.andar; if (u.numero_apartamento > maxApt) maxApt = u.numero_apartamento; });
                    const unidadesMatrix: (UnidadeComSnapshot | null)[][] = Array(maxAndar).fill(0).map(() => Array(maxApt).fill(null));
                    unidades.forEach(u => { const andarIndex = maxAndar - u.andar; const aptIndex = u.numero_apartamento - 1; if (andarIndex >= 0 && aptIndex >= 0) { unidadesMatrix[andarIndex][aptIndex] = u; } });
                    return { id: index, nome, unidadesMatrix, maxAndar, maxApt };
                });
                setTorres(torresEstruturadas);

            } catch (error) {
                console.error("Erro ao carregar dados do snapshot:", error);
                toast.error('Erro ao carregar os dados do snapshot.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [snapshot]);

    if (!snapshot) return null;

    const getDisponibilidadeStyle = (unidade: UnidadeComSnapshot) => {
        const disponibilidade = unidade.snapshotData?.disponibilidade;
        if (disponibilidade === 1) return 'bg-green-500 hover:bg-green-600';
        if (disponibilidade === 2) return 'bg-red-500 hover:bg-red-600';
        if (disponibilidade === 3) return 'bg-yellow-500 hover:bg-yellow-600';
        if (disponibilidade === 4) return 'bg-slate-600 hover:bg-slate-700';
        return 'bg-gray-400 hover:bg-gray-500';
    };

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-full flex flex-col">
                    <div className="px-6 py-4 border-b flex justify-between items-center flex-shrink-0">
                        <h2 className="text-2xl font-bold text-gray-800">
                            Visualizando Snapshot: {empreendimento?.nome}
                            <span className="block text-sm font-normal text-gray-500">Estado consolidado em {formatDate(snapshot.data_snapshot)}</span>
                        </h2>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={28} /></button>
                    </div>
                    <div className="flex-grow overflow-y-auto">
                        <div className="p-6 border-b">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-lg font-semibold text-gray-800">Dados do Empreendimento no Snapshot</h3>
                                <button
                                    onClick={() => empreendimento && setLogItem({ id: empreendimento.id, nome: empreendimento.nome, tipo: 'empreendimento' })}
                                    className="flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 font-semibold rounded-md hover:bg-indigo-200 text-sm"
                                >
                                    <Clock size={16} /> Ver Logs de Consolidação
                                </button>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div className="p-3 bg-gray-50 rounded"><strong>Área Construída:</strong> {snapshot.area_construida} m²</div>
                                <div className="p-3 bg-gray-50 rounded"><strong>Data Entrega:</strong> {formatDate(snapshot.data_entrega)}</div>
                                <div className="p-3 bg-gray-50 rounded"><strong>Início Vendas:</strong> {formatDate(snapshot.data_inicio_comercializacao)}</div>
                                <div className="p-3 bg-gray-50 rounded"><strong>Fase:</strong> {getFaseEmpreendimentoDisplay(snapshot.fase_empreendimento)}</div>
                                <div className="p-3 bg-gray-50 rounded">
                                    <strong>Tabela de Preço:</strong> {" "}
                                    {snapshot.tabela_preco ? (
                                        <a
                                            href={snapshot.tabela_preco}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-indigo-600 hover:underline"
                                        >
                                            Ver Tabela
                                        </a>
                                    ) : (
                                        "N/A"
                                    )}
                                </div>
                                <div className="p-3 bg-gray-50 rounded col-span-full"><strong>Observação:</strong> {snapshot.observacao || 'N/A'}</div>
                            </div>
                        </div>
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Unidades no Snapshot</h3>
                            {loading ? <Spinner /> : (
                                <div className="flex flex-col">
                                    <div className="border-b border-gray-200">
                                        <nav className="-mb-px flex space-x-6 overflow-x-auto">
                                            {torres.map((torre, index) => (<button key={torre.id} onClick={() => setActiveTab(index)} className={`whitespace-nowrap inline-flex items-center py-3 px-1 border-b-2 font-medium text-sm ${activeTab === index ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300'}`}><Building size={16} className="mr-2" /> {torre.nome} </button>))}
                                            {unidadesAvulsas.length > 0 && (<button onClick={() => setActiveTab(torres.length)} className={`whitespace-nowrap inline-flex items-center py-3 px-1 border-b-2 font-medium text-sm ${activeTab === torres.length ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300'}`}><Home size={16} className="mr-2" /> Unidades Avulsas ({unidadesAvulsas.length})</button>)}
                                        </nav>
                                    </div>
                                    <div className="pt-4 overflow-x-auto">
                                        {activeTab < torres.length && torres[activeTab] ? (
                                            <table className="min-w-full border-collapse">
                                                <thead><tr><th className="p-2 border text-xs font-bold text-gray-600 bg-gray-100">Andar</th>{Array.from({ length: torres[activeTab].maxApt }).map((_, i) => (<th key={i} className="p-2 border text-xs font-bold text-gray-600 bg-gray-100">Apto {String(i + 1).padStart(2, '0')}</th>))}</tr></thead>
                                                <tbody>{torres[activeTab].unidadesMatrix.map((andar, rowIndex) => (<tr key={rowIndex}><td className="p-2 border font-semibold text-center bg-gray-50">{torres[activeTab].maxAndar - rowIndex}º</td>{Array.from({ length: torres[activeTab].maxApt }).map((_, aptIndex) => { const unidade = andar[aptIndex]; return (<td key={aptIndex} className="p-1 border text-center">{unidade ? (
                                                    <div onClick={() => setLogItem({ id: unidade.id, nome: unidade.nome, tipo: 'unidade' })} 
                                                         className={`group relative w-full h-20 rounded flex flex-col items-center justify-center text-white font-bold text-xs p-1 transition-colors cursor-pointer ${getDisponibilidadeStyle(unidade)}`}>
                                                        <span>{unidade.nome}</span>
                                                        <div className="absolute bottom-1 right-1 bg-black bg-opacity-30 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <Clock size={12}/>
                                                        </div>
                                                    </div>
                                                ) : <div className="w-full h-20 bg-gray-200 rounded"></div>}</td>);})}</tr>))}</tbody>
                                            </table>
                                        ) : (
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">{unidadesAvulsas.map(unidade => (
                                                <div key={unidade.id} 
                                                     onClick={() => setLogItem({ id: unidade.id, nome: unidade.nome, tipo: 'unidade' })}
                                                     className={`group relative h-24 rounded flex flex-col items-center justify-center text-white font-bold text-sm p-2 transition-colors cursor-pointer ${getDisponibilidadeStyle(unidade)}`}>
                                                    <Home size={20} className="mb-1"/>
                                                    <span>{unidade.nome}</span>
                                                    <div className="absolute bottom-1 right-1 bg-black bg-opacity-30 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <Clock size={14}/>
                                                    </div>
                                                </div>
                                            ))}</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {logItem && <VisualizadorLogsModal item={logItem} dataSnapshot={snapshot.data_snapshot} onClose={() => setLogItem(null)} />}
        </>
    );
};

// --- COMPONENTE REUTILIZÁVEL: PaginatedTable (Sem alterações) ---

export type Column<T> = {
    key: keyof T | 'actions';
    title: string;
    render?: (value: any, record: T) => React.ReactNode;
};

interface PaginatedTableProps<T> {
    data: T[];
    columns: Column<T>[];
    loading: boolean;
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onSearch?: (search: string) => void;
    onSort?: (field: keyof T) => void;
    onRowClick?: (record: T) => void; 
}

export function PaginatedTable<T extends { id: any }>({
    data,
    columns,
    loading,
    currentPage,
    totalPages,
    onPageChange,
    onSearch,
    onSort,
    onRowClick,
}: PaginatedTableProps<T>) {
    return (
        <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
            {onSearch && (
                <div className="p-4 border-b">
                    <input
                        type="text"
                        placeholder="Buscar..."
                        onChange={(e) => onSearch(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-auto"
                    />
                </div>
            )}
            
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={String(column.key)}
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                    onClick={() => onSort && column.key !== 'actions' ? onSort(column.key as keyof T) : undefined}
                                >
                                    {column.title}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td colSpan={columns.length} className="px-6 py-10 text-center">
                                    <div className="flex justify-center items-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                    </div>
                                </td>
                            </tr>
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="px-6 py-10 text-center text-gray-500">
                                    Nenhum registro encontrado
                                </td>
                            </tr>
                        ) : (
                            data.map((record) => (
                                <tr 
                                    key={record.id} 
                                    onClick={() => onRowClick?.(record)}
                                    className={onRowClick ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}
                                >
                                    {columns.map((column) => (
                                        <td key={String(column.key)} className="px-6 py-4 whitespace-nowrap text-sm">
                                            {column.render
                                                ? column.render(record[column.key as keyof T], record)
                                                : <span className="text-gray-900">{String(record[column.key as keyof T])}</span>}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Página <span className="font-medium">{currentPage}</span> de{' '}
                                <span className="font-medium">{totalPages}</span>
                            </p>
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                    onClick={() => onPageChange(1)}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <span>««</span>
                                </button>
                                <button
                                    onClick={() => onPageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <ChevronLeft className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={() => onPageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <ChevronRight className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={() => onPageChange(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <span>»»</span>
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- COMPONENTE 1: Painel Principal de Snapshots (MODIFICADO para busca client-side) ---

const PainelSnapshots: React.FC = () => {
    const [allSnapshots, setAllSnapshots] = useState<SnapshotEmpreendimento[]>([]);
    const [empreendimentosMap, setEmpreendimentosMap] = useState<Map<number, string>>(new Map());
    const [loading, setLoading] = useState(true);
    const [snapshotAberto, setSnapshotAberto] = useState<SnapshotEmpreendimento | null>(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Carrega todos os dados necessários na montagem do componente
    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                // Carrega o mapa de nomes de empreendimentos
                const empResponse = await empreendimentoService.list({ limit: 2000 });
                const map = new Map(empResponse.results.map(e => [e.id, e.nome]));
                setEmpreendimentosMap(map);
                
                // Carrega TODOS os snapshots de empreendimento
                const snapshotResponse = await snapshotEmpreendimentoService.list({ limit: 2000, ordering: '-data_snapshot' });
                setAllSnapshots(snapshotResponse.results);

            } catch (error) {
                console.error("Falha ao carregar dados iniciais:", error);
                toast.error('Erro ao carregar o painel de snapshots.');
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, []);

    // Filtra e pagina os dados no client-side
    const { displayedSnapshots, totalPages } = useMemo(() => {
        const filtered = allSnapshots.filter(snapshot => {
            const nomeEmpreendimento = empreendimentosMap.get(snapshot.empreendimento) || '';
            return nomeEmpreendimento.toLowerCase().includes(searchTerm.toLowerCase());
        });

        const total = Math.ceil(filtered.length / itemsPerPage);
        
        const paginated = filtered.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );

        return { displayedSnapshots: paginated, totalPages: total };
    }, [allSnapshots, empreendimentosMap, searchTerm, currentPage]);

    // Reseta para a primeira página sempre que uma nova busca é feita
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [searchTerm]);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };
    
    const columns: Column<SnapshotEmpreendimento>[] = [
        {
            key: 'empreendimento',
            title: 'Empreendimento',
            render: (_, record) => (
                <span className="font-medium text-gray-900">
                    {empreendimentosMap.get(record.empreendimento) || `ID: ${record.empreendimento}`}
                </span>
            ),
        },
        {
            key: 'data_snapshot',
            title: 'Data do Snapshot',
            render: (value) => <span className="text-gray-600">{formatDate(value)}</span>,
        },
        {
            key: 'fase_empreendimento',
            title: 'Fase',
            render: (value) => (
                <span className="text-gray-600">{getFaseEmpreendimentoDisplay(value)}</span>
            ),
        },
    ];

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Painel de Snapshots</h1>
            
            <div className="mb-6 max-w-sm">
                 <label htmlFor="empreendimento-search" className="block text-sm font-medium text-gray-700 mb-2">
                     <Search size={16} className="inline mr-2" />
                     Buscar por Empreendimento
                 </label>
                 <input 
                     id="empreendimento-search" 
                     type="text"
                     value={searchTerm} 
                     onChange={(e) => setSearchTerm(e.target.value)} 
                     placeholder="Digite o nome do empreendimento..."
                     className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md shadow-sm"
                 />
            </div>

            <PaginatedTable<SnapshotEmpreendimento>
                columns={columns}
                data={displayedSnapshots}
                loading={loading}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                onRowClick={(snapshot) => setSnapshotAberto(snapshot)}
            />

            {snapshotAberto && <SnapshotViewerModal snapshot={snapshotAberto} onClose={() => setSnapshotAberto(null)} />}
        </div>
    );
};

export default PainelSnapshots;